import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, LogOut, RefreshCw, Mail, Phone, Calendar, User, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (data.success) {
        setSubmissions(data.data);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        toast.error('Session expired. Please login again.');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch submissions');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSubmissions();
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (_) {}
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ChefHat className="w-7 h-7 text-orange-600" />
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="total-inquiries-card">
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-bold text-orange-600">{submissions.length}</p>
              <p className="text-gray-500 mt-1">Total Inquiries</p>
            </CardContent>
          </Card>
          <Card data-testid="new-inquiries-card">
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-bold text-green-600">{submissions.filter(s => s.status === 'new').length}</p>
              <p className="text-gray-500 mt-1">New Inquiries</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-3xl font-bold text-blue-600">{submissions.length > 0 ? formatDate(submissions[0]?.created_at) : 'N/A'}</p>
              <p className="text-gray-500 mt-1">Latest Inquiry</p>
            </CardContent>
          </Card>
        </div>

        {/* Submissions List */}
        <h2 className="text-lg font-bold text-gray-900 mb-4">Contact Submissions</h2>
        {submissions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg">No contact submissions yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {submissions.map((sub, idx) => (
              <Card key={sub.id || idx} className="hover:shadow-md transition-shadow" data-testid={`submission-card-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-orange-600" />
                        <span className="font-semibold text-gray-900">{sub.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sub.status === 'new' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {sub.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{sub.email}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{sub.phone}</span>
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(sub.created_at)}</span>
                      </div>
                      <p className="text-gray-700 mt-2 bg-gray-50 p-3 rounded-lg">{sub.message}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
